import { useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// ── Tipos públicos ──

export interface MapCluster {
  id: string;
  name: string;
  color: string;
  sede_municipio_ibge: string | null;
  hub_count: number;
  work_count: number;
}

export interface MapHub {
  id: string;
  name: string;
  cluster_id: string;
  cluster_name: string;
  cluster_color: string;
  municipality_code: string;
  coordinates: [number, number];
  status: 'active' | 'inactive' | 'maintenance';
  capacity: number;
  has_valid_coordinates: boolean;
}

export interface MapWork {
  id: string;
  name: string;
  municipality_code: string;
  coordinates: [number, number];
  status: string;
  type: string;
  progress: number;
  contract_name: string;
  cluster_name: string;
  has_valid_coordinates: boolean;
}

export interface MapDataStats {
  total_clusters: number;
  total_hubs: number;
  total_works: number;
  hubs_without_coordinates: number;
  works_without_coordinates: number;
}

// ── Query key exportado para invalidação por hooks de CRUD ──
export const MAP_DATA_QUERY_KEY = ['map-data'] as const;

// ── Coordenada padrão (centro geográfico do Brasil) — último recurso ──
const BRAZIL_CENTER: [number, number] = [-52, -15];

// ── Debounce de invalidação Realtime (ms) ──
const REALTIME_DEBOUNCE_MS = 3000;

// ── Funções utilitárias (puras, sem estado) ──

const isValidCoordinate = (coords: number[] | null): coords is [number, number] => {
  if (!coords || !Array.isArray(coords) || coords.length < 2) return false;
  const [lng, lat] = coords;
  if (lng === 0 && lat === 0) return false;
  return lng >= -74 && lng <= -34 && lat >= -34 && lat <= 5;
};

// ── Fetch da tabela municipio_coordenadas (fonte única de fallback) ──

type CoordMap = Map<string, [number, number]>;

async function fetchMunicipalityCoordinates(): Promise<CoordMap> {
  const { data, error } = await supabase
    .from('municipio_coordenadas')
    .select('codigo_ibge, longitude, latitude');

  if (error) {
    console.warn('[MapData] Erro ao buscar municipio_coordenadas:', error.message);
    return new Map();
  }

  const map = new Map<string, [number, number]>();
  (data || []).forEach(row => {
    const lng = Number(row.longitude);
    const lat = Number(row.latitude);
    if (!isNaN(lng) && !isNaN(lat)) {
      map.set(row.codigo_ibge, [lng, lat]);
    }
  });
  return map;
}

// ── Resolve coordenadas: entidade → municipio_coordenadas → centro do Brasil ──

function resolveCoordinates(
  dbCoords: number[] | null,
  municipalityCode: string,
  coordMap: CoordMap
): { coords: [number, number]; valid: boolean } {
  if (isValidCoordinate(dbCoords)) {
    return { coords: [dbCoords[0], dbCoords[1]], valid: true };
  }
  const fallback = coordMap.get(municipalityCode);
  if (fallback) {
    return { coords: fallback, valid: true };
  }
  return { coords: BRAZIL_CENTER, valid: false };
}

// ── Fetch functions (async puras — sem side effects) ──

async function fetchMapClusters(): Promise<MapCluster[]> {
  const { data, error } = await supabase
    .from('clusters')
    .select('*')
    .order('name');

  if (error) throw error;

  const { data: hubCounts } = await supabase
    .from('hubs')
    .select('cluster_id');

  const { data: contractData } = await supabase
    .from('contracts')
    .select('id, cluster_id');

  const contractIds = contractData?.map(c => c.id) || [];

  let workCounts: { contract_id: string }[] = [];
  if (contractIds.length > 0) {
    const { data: worksData } = await supabase
      .from('works')
      .select('contract_id')
      .in('contract_id', contractIds);
    workCounts = worksData || [];
  }

  const clusterHubCount: Record<string, number> = {};
  hubCounts?.forEach(h => {
    clusterHubCount[h.cluster_id] = (clusterHubCount[h.cluster_id] || 0) + 1;
  });

  const contractClusterMap: Record<string, string> = {};
  contractData?.forEach(c => {
    contractClusterMap[c.id] = c.cluster_id;
  });

  const clusterWorkCount: Record<string, number> = {};
  workCounts.forEach(w => {
    const clusterId = contractClusterMap[w.contract_id];
    if (clusterId) {
      clusterWorkCount[clusterId] = (clusterWorkCount[clusterId] || 0) + 1;
    }
  });

  return (data || []).map(c => ({
    id: c.id,
    name: c.name,
    color: c.color,
    sede_municipio_ibge: c.sede_municipio_ibge,
    hub_count: clusterHubCount[c.id] || 0,
    work_count: clusterWorkCount[c.id] || 0,
  }));
}

async function fetchMapHubs(coordMap: CoordMap): Promise<MapHub[]> {
  const { data, error } = await supabase
    .from('hubs')
    .select(`
      id, name, cluster_id, municipality_code, 
      coordinates, status, capacity,
      clusters!inner(name, color)
    `)
    .order('name');

  if (error) throw error;

  return (data || []).map(h => {
    const { coords, valid } = resolveCoordinates(
      h.coordinates as number[],
      h.municipality_code,
      coordMap
    );

    return {
      id: h.id,
      name: h.name,
      cluster_id: h.cluster_id,
      cluster_name: (h.clusters as any).name,
      cluster_color: (h.clusters as any).color,
      municipality_code: h.municipality_code,
      coordinates: coords,
      status: h.status as 'active' | 'inactive' | 'maintenance',
      capacity: h.capacity,
      has_valid_coordinates: valid,
    };
  });
}

async function fetchMapWorks(coordMap: CoordMap): Promise<MapWork[]> {
  const { data, error } = await supabase
    .from('works')
    .select(`
      id, name, municipality_code, coordinates,
      status, type, progress,
      contracts!inner(name, cluster_id, clusters!inner(name))
    `)
    .order('name');

  if (error) throw error;

  return (data || []).map(w => {
    const { coords, valid } = resolveCoordinates(
      w.coordinates as number[],
      w.municipality_code,
      coordMap
    );

    return {
      id: w.id,
      name: w.name,
      municipality_code: w.municipality_code,
      coordinates: coords,
      status: w.status,
      type: w.type,
      progress: w.progress,
      contract_name: (w.contracts as any).name,
      cluster_name: (w.contracts as any).clusters.name,
      has_valid_coordinates: valid,
    };
  });
}

// ── Orquestrador de fetch (4 queries paralelas) ──

interface MapData {
  clusters: MapCluster[];
  hubs: MapHub[];
  works: MapWork[];
  stats: MapDataStats;
}

async function fetchAllMapData(): Promise<MapData> {
  const [coordMap, clusters] = await Promise.all([
    fetchMunicipalityCoordinates(),
    fetchMapClusters(),
  ]);

  const [hubs, works] = await Promise.all([
    fetchMapHubs(coordMap),
    fetchMapWorks(coordMap),
  ]);

  return {
    clusters,
    hubs,
    works,
    stats: {
      total_clusters: clusters.length,
      total_hubs: hubs.length,
      total_works: works.length,
      hubs_without_coordinates: hubs.filter(h => !h.has_valid_coordinates).length,
      works_without_coordinates: works.filter(w => !w.has_valid_coordinates).length,
    },
  };
}

// ── Defaults ──

const DEFAULT_STATS: MapDataStats = {
  total_clusters: 0,
  total_hubs: 0,
  total_works: 0,
  hubs_without_coordinates: 0,
  works_without_coordinates: 0,
};

// ── Hook principal ──

export function useMapData() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastWarningRef = useRef('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: MAP_DATA_QUERY_KEY,
    queryFn: fetchAllMapData,
    staleTime: 5 * 60 * 1000,
  });

  // Invalidação debounced — agrupa múltiplos eventos Realtime (ex: lotes de geometria)
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: MAP_DATA_QUERY_KEY });
    }, REALTIME_DEBOUNCE_MS);
  }, [queryClient]);

  // Assinatura Realtime — propaga alterações de outros administradores
  useEffect(() => {
    const channel = supabase
      .channel('map-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'clusters' },
        () => debouncedInvalidate()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'hubs' },
        () => debouncedInvalidate()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'works' },
        () => debouncedInvalidate()
      )
      .subscribe();

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [debouncedInvalidate]);

  // Aviso de dados sem coordenadas (deduplicado por valores reais)
  useEffect(() => {
    if (!data) return;
    const { stats } = data;
    const warningKey = `${stats.hubs_without_coordinates}-${stats.works_without_coordinates}`;
    if (
      (stats.hubs_without_coordinates > 0 || stats.works_without_coordinates > 0) &&
      warningKey !== lastWarningRef.current
    ) {
      lastWarningRef.current = warningKey;
      toast({
        variant: 'default',
        title: 'Aviso de dados',
        description: `${stats.hubs_without_coordinates} hub(s) e ${stats.works_without_coordinates} obra(s) sem coordenadas válidas.`,
      });
    }
  }, [data, toast]);

  return {
    clusters: data?.clusters ?? [],
    hubs: data?.hubs ?? [],
    works: data?.works ?? [],
    stats: data?.stats ?? DEFAULT_STATS,
    loading: isLoading,
    refetch,
  };
}
